import type { TenantStatus } from '@bixo/ui'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { PlatformLayout } from './components/platform-layout'
import { Root } from './components/root'
import { meQuery } from './lib/api'
import { LoginPage } from './routes/login'
import { OverviewPage } from './routes/overview'

export interface RouterContext {
  queryClient: QueryClient
}

const STATUSES: TenantStatus[] = ['provisioning', 'active', 'suspended', 'failed']

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Root })

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? { redirect: search.redirect }
      : {},
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQuery)) throw redirect({ to: '/' })
  },
  component: LoginPage,
})

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: async ({ context, location }) => {
    if (!(await context.queryClient.ensureQueryData(meQuery))) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: PlatformLayout,
})

const overviewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: OverviewPage,
})

interface TenantsSearch {
  create?: boolean
  tenant?: string
  status?: TenantStatus
}

const tenantsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants',
  validateSearch: (search: Record<string, unknown>): TenantsSearch => ({
    ...(search.create === true || search.create === 'true' ? { create: true } : {}),
    ...(typeof search.tenant === 'string' && search.tenant !== '' ? { tenant: search.tenant } : {}),
    ...(STATUSES.includes(search.status as TenantStatus) ? { status: search.status as TenantStatus } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/tenants'), 'TenantsPage'),
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: lazyRouteComponent(() => import('./routes/signup'), 'SignupPage'),
})

const plansRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('./routes/plans'), 'PlansPage'),
})

// The old invoices page lives on as the Billing page's first tab.
const invoicesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/invoices',
  beforeLoad: () => {
    throw redirect({ to: '/billing', search: { tab: 'invoices' } })
  },
})

const BILLING_TABS = ['invoices', 'payments', 'subscriptions', 'checkouts', 'trials', 'dunning'] as const
type BillingTab = (typeof BILLING_TABS)[number]

const billingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/billing',
  validateSearch: (search: Record<string, unknown>): { tab?: BillingTab } =>
    BILLING_TABS.includes(search.tab as BillingTab) ? { tab: search.tab as BillingTab } : {},
  component: lazyRouteComponent(() => import('./routes/billing'), 'BillingPage'),
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants/$tenantId',
  component: lazyRouteComponent(() => import('./routes/tenant-detail'), 'TenantDetailPage'),
})

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./routes/settings'), 'SettingsPage'),
})

const announcementsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/announcements',
  component: lazyRouteComponent(() => import('./routes/announcements'), 'AnnouncementsPage'),
})

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('./routes/users'), 'UsersPage'),
})

// The page was called Operators before roles existed.
const operatorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operators',
  beforeLoad: () => {
    throw redirect({ to: '/users' })
  },
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('./routes/password-reset'), 'ForgotPasswordPage'),
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { token?: string; email?: string } => ({
    ...(typeof search.token === 'string' && search.token !== '' ? { token: search.token } : {}),
    ...(typeof search.email === 'string' && search.email !== '' ? { email: search.email } : {}),
  }),
  component: lazyRouteComponent(() => import('./routes/password-reset'), 'ResetPasswordPage'),
})

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('./routes/audit'), 'PlatformAuditPage'),
})

const securityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/security',
  component: lazyRouteComponent(() => import('./routes/security'), 'SecurityPage'),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  operatorsRoute,
  shellRoute.addChildren([
    overviewRoute,
    tenantsRoute,
    tenantDetailRoute,
    plansRoute,
    invoicesRoute,
    billingRoute,
    settingsRoute,
    announcementsRoute,
    usersRoute,
    auditRoute,
    securityRoute,
  ]),
])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
